import { doc, getDoc, collection, getDocs, setDoc, deleteDoc, query, where, updateDoc, arrayUnion, arrayRemove, serverTimestamp, deleteField } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFirebase, getProdDb } from './firebase';

export const getUserData = async (uid) => {
  const db = getProdDb();
  try {
    if (!db) {
      throw new Error('Firestore is not initialized.');
    }
    const userDoc = await getDoc(doc(db, 'cUsers', uid));
    return userDoc.exists() ? userDoc.data() : null;
  } catch (error) {
    console.error(`Error fetching user data for UID ${uid}:`, error);
    throw error;
  }
};

export const readUserCollection = async (collectionName, uid) => {
  // 'profile' is always the Prod project; all other user collections follow the user's env.
  const db = collectionName === 'profile' ? getProdDb() : getFirebase().db;
  try {
    if (!db) {
      throw new Error('Firestore is not initialized.');
    }
    if (collectionName === 'profile') {
      const docRef = doc(db, collectionName, uid);
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        return [{ id: docSnapshot.id, ...data }];
      }
      return [];
    } else {
      const q = query(collection(db, collectionName), where('userId', '==', uid));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    }
  } catch (error) {
    console.error(`Error reading ${collectionName} for user ${uid}:`, error);
    throw error;
  }
};

export const updateProfileUsage = async (profileId, userId, collectionName, isAdded) => {
  const db = getFirebase().db;
  try {
    if (!db) {
      throw new Error('Firestore is not initialized.');
    }
    if (!['custom', 'favourites', 'enquiry', 'order'].includes(collectionName)) {
      throw new Error(`Invalid collection name: ${collectionName}`);
    }

    const usageDocRef = doc(db, 'profileUsage', profileId);
    const usageDoc = await getDoc(usageDocRef);
    let currentData = usageDoc.exists() ? usageDoc.data() : { usedBy: [], usageFlags: {} };
    let usedBy = currentData.usedBy || [];
    let usageFlags = currentData.usageFlags || {};

    // Initialize usage flags for the user if not present
    if (!usageFlags[userId]) {
      usageFlags[userId] = {
        inCustom: false,
        inFavourites: false,
        inEnquiry: false,
        inOrder: false,
        firstUsedAt: null,
        lastUsedAt: null,
      };
    }

    // Update the specific flag based on the collection
    const flagField = collectionName === 'custom' ? 'inCustom' : collectionName === 'favourites' ? 'inFavourites' :
     collectionName === 'enquiry' ? 'inEnquiry' : 'inOrder';
    usageFlags[userId][flagField] = isAdded;

    // Update firstUsedAt if this is the first usage
    if (isAdded && !usageFlags[userId].firstUsedAt) {
      usageFlags[userId].firstUsedAt = serverTimestamp();
    }

    // Update lastUsedAt if the flag is being set to true
    if (isAdded) {
      usageFlags[userId].lastUsedAt = serverTimestamp();
    } else {
      // If setting to false, check if there are still any true flags
      const userStillUsing = usageFlags[userId].inCustom || usageFlags[userId].inFavourites || usageFlags[userId].inEnquiry || usageFlags[userId].inOrder;
      if (!userStillUsing) {
        // Keep lastUsedAt as-is (it reflects the last true entry)
      } else {
        // If there are still true flags, we don't update lastUsedAt unless a new true entry is made
      }
    }

    const userStillUsing = usageFlags[userId].inCustom || usageFlags[userId].inEnquiry || usageFlags[userId].inFavourites || usageFlags[userId].inOrder;

    if (isAdded) {
      if (!usedBy.includes(userId)) {
        usedBy = [...usedBy, userId];
      }
      await updateDoc(usageDocRef, {
        usedBy: usedBy,
        [`usageFlags.${userId}`]: usageFlags[userId],
        updated: serverTimestamp(),
      }).catch(async (error) => {
        if (error.code === 'not-found') {
          await setDoc(usageDocRef, {
            profileId,
            usedBy,
            usageFlags,
            created: serverTimestamp(),
            updated: serverTimestamp(),
          });
        } else {
          throw error;
        }
      });
    } else {
      if (!userStillUsing) {
        usedBy = usedBy.filter(id => id !== userId);

        if (usedBy.length === 0) {
          // If no users are using the profile, delete the document
          await deleteDoc(usageDocRef);
          return; // Exit the function after deletion
        }

        // Otherwise, update the document as before
        await updateDoc(usageDocRef, {
          usedBy: usedBy,
          [`usageFlags.${userId}`]: deleteField(),
          updated: serverTimestamp(),
        }).catch(async (error) => {
          if (error.code === 'not-found') {
            console.warn('Document not found during update - likely deleted already');
          } else {
            throw error;
          }
        });
      } else {
        await updateDoc(usageDocRef, {
          usedBy: usedBy,
          [`usageFlags.${userId}`]: usageFlags[userId],
          updated: serverTimestamp(),
        }).catch(async (error) => {
          if (error.code === 'not-found') {
            await setDoc(usageDocRef, {
              profileId,
              usedBy,
              usageFlags,
              created: serverTimestamp(),
              updated: serverTimestamp(),
            });
          } else {
            throw error;
          }
        });
      }
    }

    const updatedDoc = await getDoc(usageDocRef);
  } catch (error) {
    console.error(`Error updating profile usage for profile ${profileId}, user ${userId}:`, error);
    throw error;
  }
};

export const uploadImageToStorage = async (uid, file) => {
  const { storage } = getFirebase();
  try {
    if (!storage) {
      throw new Error('Firebase Storage is not initialized.');
    }
    if (!file || !(file instanceof Blob)) {
      throw new Error('Invalid file object.');
    }
    const timestamp = Date.now();
    const ext = file.name
      ? file.name.split('.').pop()
      : (file.type ? file.type.split('/')[1] : 'jpg');
    const fileName = file.name || `image_${timestamp}.${ext}`;
    const storageRef = ref(storage, `custom_images/${uid}/${timestamp}_${fileName}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error(`Error uploading image for UID ${uid}, file ${file?.name}:`, error);
    throw error;
  }
};

export const writeUserDocument = async (collectionName, uid, profileData, docId = null) => {
  const db = getFirebase().db;
  try {
    if (!db) {
      throw new Error('Firestore is not initialized.');
    }

    let globalPId = profileData.globalId;
    const docRef = docId ? doc(db, collectionName, docId) : doc(collection(db, collectionName));

    if (docId) {
      // Check if the document exists
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        // Update existing document
        if (!globalPId && docSnapshot.data()?.globalId) {
          globalPId = docSnapshot.data().globalId;
        }
        const updateData = {
          ...profileData,
          userId: uid,
          id: docRef.id,
          updated: serverTimestamp(),
        };
        await updateDoc(docRef, updateData);
      } else {
        // Create new document with the specified docId
        await setDoc(docRef, {
          ...profileData,
          userId: uid,
          id: docRef.id,
          created: serverTimestamp(),
          updated: serverTimestamp(),
        });
      }
    } else {
      // Create new document with auto-generated ID
      await setDoc(docRef, {
        ...profileData,
        userId: uid,
        id: docRef.id,
        globalId: !profileData.globalId ? docRef.id : profileData.globalId,
        created: serverTimestamp(),
        updated: serverTimestamp(),
      });
    }

    if (['custom', 'enquiry', 'favourites'].includes(collectionName) && globalPId) {
      await updateProfileUsage(globalPId, uid, collectionName, true);
    }

    return docRef.id;
  } catch (error) {
    console.error(`Error writing to ${collectionName} for user ${uid}:`, error);
    throw error;
  }
};

export const deleteUserDocument = async (collectionName, uid, docId) => {
  const db = getFirebase().db;
  try {
    if (!db) {
      throw new Error('Firestore is not initialized.');
    }
    const docRef = doc(db, collectionName, docId);
    const docSnapshot = await getDoc(docRef);
    const docData = docSnapshot.exists() ? docSnapshot.data() : null;
    await deleteDoc(docRef);

    if (collectionName === 'custom' && docData && !docData.isFromHome) {
      const { storage } = getFirebase();
      if (storage) {
        const urlsToDelete = [];
        if (docData.cimage && docData.cimage.includes('custom_images')) urlsToDelete.push(docData.cimage);
        if (Array.isArray(docData.images)) {
          docData.images.forEach(url => { if (url && url.includes('custom_images')) urlsToDelete.push(url); });
        }
        await Promise.all(urlsToDelete.map(url => deleteObject(ref(storage, url)).catch(() => {})));
      }
    }

    if (['custom', 'enquiry', 'favourites', 'order'].includes(collectionName) && docData?.globalId) {
      try {
        await updateProfileUsage(docData.globalId, uid, collectionName, false);
      } catch (usageErr) {
        // Non-fatal: doc was deleted successfully; log but don't surface to caller
        console.warn(`updateProfileUsage cleanup failed for ${docId} (${collectionName}):`, usageErr?.message);
      }
    }
  } catch (error) {
    console.error(`Error deleting document ${docId} from ${collectionName} for user ${uid}:`, error);
    throw error;
  }
};

export const moveUserDocument = async (fromCollection, toCollection, uid, docId) => {
  const db = getFirebase().db;
  try {
    if (!db) {
      throw new Error('Firestore is not initialized.');
    }

    const allowedCollections = ['custom', 'favourites', 'enquiry', 'order', 'oldOrder'];
    if (!allowedCollections.includes(fromCollection) || !allowedCollections.includes(toCollection)) {
      throw new Error(`Invalid collection names: ${fromCollection} to ${toCollection}`);
    }

    const sourceDocRef = doc(db, fromCollection, docId);
    const sourceDocSnapshot = await getDoc(sourceDocRef);
    if (!sourceDocSnapshot.exists()) {
      throw new Error(`Document ${docId} not found in ${fromCollection}`);
    }
    const docData = sourceDocSnapshot.data();
    const targetDocRef = doc(db, toCollection, docId);

    let targetData;
    if (toCollection === 'oldOrder') {
      // Preserve all fields as-is; only update housekeeping timestamps
      targetData = { ...docData, userId: uid, id: targetDocRef.id, updated: serverTimestamp() };
    } else if (fromCollection === 'custom' && toCollection === 'enquiry') {
      // Build a clean enquiry document with only the fields the enquiry flow needs.
      // cimage (Super single image) is promoted into the images array if no images array exists.
      const enquiryImages =
        Array.isArray(docData.images) && docData.images.length > 0
          ? docData.images
          : docData.cimage
          ? [docData.cimage]
          : [];
      targetData = {
        userId: uid,
        id: targetDocRef.id,
        addedAt: serverTimestamp(),
        created: docData.created || serverTimestamp(),
        updated: serverTimestamp(),
        reqState: 'New Request',
        globalId: docData.globalId || null,
        theme: docData.theme || null,
        cover: docData.cover || null,
        frosting: docData.frosting || null,
        eggless: docData.eggless || false,
        glutenFree: docData.glutenFree || false,
        quantity: docData.quantity || null,
        ccost: docData.ccost || null,
        costest: docData.costest || null,
        images: enquiryImages,
        tiers: docData.tiers || [],
        tierCount: docData.tierCount || 1,
        isFromHome: docData.isFromHome || false,
      };
    } else {
      targetData = {
        ...docData,
        userId: uid,
        id: targetDocRef.id,
        created: docData.created || serverTimestamp(),
        updated: serverTimestamp(),
        addedAt: ['enquiry', 'order'].includes(toCollection) ? serverTimestamp() : docData.addedAt,
        reqState: toCollection === 'enquiry' ? 'New Request' : toCollection === 'order' ? 'Order Received' : null,
        del_address: toCollection !== 'order' ? null : docData.del_address,
        deliveryDate: toCollection !== 'order' ? null : docData.deliveryDate,
        deliveryTime: toCollection !== 'order' ? null : docData.deliveryTime,
        del_mode: toCollection !== 'order' ? false : docData.del_mode,
        disp_msg: toCollection !== 'order' ? null : docData.disp_msg ? docData.disp_msg : null,
        msg_pos: toCollection !== 'order' ? null : docData.msg_pos ? docData.msg_pos : 'Best Fit',
        rev_feed: toCollection !== 'order' ? null : docData.rev_feed ? docData.rev_feed : null,
      };
    }
    await setDoc(targetDocRef, targetData);
    
    await deleteDoc(sourceDocRef);

    if (docData.globalId) {
      try {
        await updateProfileUsage(docData.globalId, uid, fromCollection, false);
        if (toCollection !== 'oldOrder') {
          await updateProfileUsage(docData.globalId, uid, toCollection, true);
        }
      } catch (usageErr) {
        console.warn(`updateProfileUsage cleanup failed for ${docId} (move ${fromCollection}→${toCollection}):`, usageErr?.message);
      }
    } 
    return targetDocRef.id;
  } catch (error) {
    console.error(`Error moving document ${docId} from ${fromCollection} to ${toCollection} for user ${uid}:`, error);
    throw error;
  }
};